import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Smartphone, X } from "lucide-react";
import { usePlatform } from "@/contexts/PlatformContext";

const APP_PACKAGE_ID = "br.com.lordofthequiz.app";
const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${APP_PACKAGE_ID}`;
const DISMISS_KEY = "lotq_app_install_banner_until";
const DISMISS_DAYS = 30;

function isAndroidMobile() {
  return /Android/i.test(navigator.userAgent || "") && window.matchMedia("(max-width: 767px)").matches;
}

function bannerIsDismissed() {
  const until = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
  return Number.isFinite(until) && until > Date.now();
}

export default function AppInstallBanner() {
  const { isApp } = usePlatform();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const updateVisibility = () => setVisible(!isApp && isAndroidMobile() && !bannerIsDismissed());
    updateVisibility();
    window.addEventListener("resize", updateVisibility);
    return () => window.removeEventListener("resize", updateVisibility);
  }, [isApp]);

  if (!visible) return null;

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000));
    setVisible(false);
  };

  const openApp = () => {
    const destination = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const fallback = encodeURIComponent(PLAY_STORE_URL);
    // The native update will register this HTTPS intent. Until then, Android falls back to Play Store.
    window.location.href = `intent://lordofthequiz.com.br${destination}#Intent;scheme=https;package=${APP_PACKAGE_ID};S.browser_fallback_url=${fallback};end`;
  };

  const isImmersiveRoute = /^(\/sala-evento\/.*\/overlay|\/apresentador\/eventos\/.*\/(qr|podium)|\/contratar\/on-demand\/projetos\/.*\/apresentacao)/.test(location.pathname);
  if (isImmersiveRoute) return null;

  return (
    <aside className="fixed inset-x-3 bottom-3 z-[90] rounded-2xl border border-[#fccf1f]/35 bg-zinc-950/95 p-3 shadow-2xl backdrop-blur md:hidden" aria-label="Aplicativo Lord of the Quiz">
      <button type="button" onClick={dismiss} className="absolute right-2 top-2 rounded-full p-1.5 text-zinc-400 hover:bg-white/10 hover:text-white" aria-label="Fechar convite do aplicativo">
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-3 pr-7">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fccf1f] text-black"><Smartphone className="h-5 w-5" /></div>
        <div className="min-w-0">
          <p className="font-outfit text-sm font-bold text-white">Leve o Quiz com você</p>
          <p className="mt-0.5 text-xs leading-snug text-zinc-400">Abra no app ou instale pela Play Store.</p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button type="button" onClick={openApp} className="rounded-xl bg-[#fccf1f] px-3 py-2 text-xs font-bold text-black transition hover:bg-[#e3ba1b]">Abrir app</button>
        <a href={PLAY_STORE_URL} className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/15 px-3 py-2 text-xs font-bold text-white transition hover:bg-white/10">
          <Download className="h-3.5 w-3.5" /> Instalar
        </a>
      </div>
    </aside>
  );
}
